import { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { messageOf } from "../../api";
import { Notice } from "../../parts";
import { useRequestRefund } from "./useClassroom";

/**
 * "This session was cancelled by the school": the booking stays, and the
 * customer chooses to move it to another session or take a full refund to
 * their original payment method. The refund asks first, in place (v1 used
 * an AlertDialog, which would portal out of the shadow root). Port of v1's
 * VirtualClassroom orphaned-session banner.
 */
export function OrphanedBanner(p: {
  api: string;
  bookingId: string;
  rescheduleHref: string | null;
  canRefund: boolean;
  /** The page says so above the receipt: the banner itself goes once the
   * refreshed booking can no longer be refunded. */
  onRefunded: () => void;
}) {
  const refund = useRequestRefund(p.api, p.bookingId);
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div className="flex-1 space-y-3">
          <div>
            <p className="font-semibold">This session was cancelled by the school</p>
            <p className="mt-1 text-sm text-amber-800">You can choose what to do with this booking:</p>
          </div>
          {!confirming && (
            <div className="flex flex-col gap-2 sm:flex-row">
              {p.rescheduleHref && (
                <Button asChild variant="outline" className="border-amber-300 bg-white hover:bg-amber-100">
                  <a href={p.rescheduleHref}>Reschedule to another session</a>
                </Button>
              )}
              {p.canRefund && (
                <Button variant="outline" className="border-amber-300 bg-white hover:bg-amber-100" onClick={() => setConfirming(true)}>
                  Request a refund
                </Button>
              )}
            </div>
          )}
          {confirming && (
            <div className="space-y-3 rounded-md border border-amber-200 bg-white p-4 text-foreground" role="group" aria-label="Request a refund?">
              <p className="font-semibold">Request a refund?</p>
              <p className="text-sm text-muted-foreground">
                You’ll be refunded to your original payment method. Refunds usually take 5–10 business days to appear, depending on your bank.
              </p>
              {refund.isError && (
                <Notice tone="error">{messageOf(refund.error, "Could not process the refund. Please contact the school.")}</Notice>
              )}
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => refund.mutate(undefined, { onSuccess: p.onRefunded })} disabled={refund.isPending}>
                  {refund.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Yes, refund my booking
                </Button>
                <Button variant="ghost" onClick={() => setConfirming(false)} disabled={refund.isPending}>
                  Not now
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
